import React, { useState, useEffect, useRef } from "react";
import "./KnowledgeBasePanel.css";

interface RAGChunk {
  content: string;
  source: string;
  page_number?: number;
  score: number;
  chunk_id: string;
}

interface KnowledgeBasePanelProps {
  chunks: RAGChunk[];
  isOpen: boolean;
  onClose: () => void;
  collapseAll?: boolean;
}

const KnowledgeBasePanel: React.FC<KnowledgeBasePanelProps> = ({
  chunks,
  isOpen,
  onClose,
  collapseAll = false,
}) => {
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const chunkHeaderRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Collapse all chunks when collapseAll prop changes to true
  useEffect(() => {
    if (collapseAll) {
      setExpandedChunks(new Set());
    }
  }, [collapseAll]);

  const toggleChunk = (chunkId: string) => {
    setExpandedChunks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(chunkId)) {
        newSet.delete(chunkId);
      } else {
        newSet.add(chunkId);
      }
      return newSet;
    });
  };

  const navigateToChunk = (chunkIndex: number) => {
    if (chunkIndex >= 0 && chunkIndex < chunks.length) {
      const chunk = chunks[chunkIndex];
      const chunkId = chunk.chunk_id || `chunk-${chunkIndex}`;

      // Expand the chunk
      setExpandedChunks((prev) => {
        const newSet = new Set(prev);
        newSet.add(chunkId);
        return newSet;
      });

      // Scroll to the chunk after a short delay to allow for expansion
      setTimeout(() => {
        const chunkHeaderElement = chunkHeaderRefs.current[chunkId];
        if (chunkHeaderElement) {
          chunkHeaderElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
          // Add a highlight effect to the parent chunk
          const chunkElement = chunkHeaderElement.parentElement;
          if (chunkElement) {
            chunkElement.classList.add("kb-chunk--highlighted");
            setTimeout(() => {
              chunkElement.classList.remove("kb-chunk--highlighted");
            }, 2000);
          }
        }
      }, 100);
    }
  };

  // Listen for navigation events
  useEffect(() => {
    const handleNavigateToChunk = (e: CustomEvent) => {
      const { chunkIndex } = e.detail;
      navigateToChunk(chunkIndex);
    };

    window.addEventListener(
      "navigateToChunk",
      handleNavigateToChunk as EventListener
    );
    return () => {
      window.removeEventListener(
        "navigateToChunk",
        handleNavigateToChunk as EventListener
      );
    };
  }, [chunks]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="kb-panel-overlay" onClick={onClose} />

      {/* Panel */}
      <div className={`kb-panel ${isOpen ? "kb-panel--open" : ""}`}>
        {/* Header */}
        <div className="kb-panel__header">
          <div className="kb-panel__title">
            <span className="kb-panel__icon">📚</span>
            <h3>Retrieved Sources</h3>
            <span className="kb-panel__count">{chunks.length}</span>
          </div>
          <button className="kb-panel__close" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="kb-panel__content">
          {chunks.length === 0 ? (
            <div className="kb-panel__empty">
              <span className="kb-panel__empty-icon">📭</span>
              <p>No sources retrieved</p>
            </div>
          ) : (
            <div className="kb-panel__chunks">
              {chunks.map((chunk, index) => {
                const chunkId = chunk.chunk_id || `chunk-${index}`;
                const isExpanded = expandedChunks.has(chunkId);

                return (
                  <div key={chunkId} className="kb-chunk">
                    <div
                      className="kb-chunk__header"
                      onClick={() => toggleChunk(chunkId)}
                      ref={(el) => {
                        chunkHeaderRefs.current[chunkId] = el;
                      }}
                    >
                      <div className="kb-chunk__meta">
                        <span className="kb-chunk__toggle">
                          {isExpanded ? "▼" : "▶"}
                        </span>
                        <span className="kb-chunk__number">#{index + 1}</span>
                        <div className="kb-chunk__source">
                          <span className="kb-chunk__source-icon">📄</span>
                          <span
                            className="kb-chunk__source-text"
                            title={chunk.source}
                          >
                            {chunk.source}
                          </span>
                        </div>
                        {chunk.page_number && (
                          <span className="kb-chunk__page-badge">
                            Page {chunk.page_number}
                          </span>
                        )}
                      </div>
                      <span className="kb-chunk__score">
                        {(chunk.score * 100).toFixed(0)}%
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="kb-chunk__content">{chunk.content}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default KnowledgeBasePanel;
